import { useEffect, useState } from 'react';
import './scout-report.styles.scss';
import { Link, useParams } from 'react-router-dom';
import { DEFENSES, OFFENSES, PACES, strategyLabel } from '@shared/strategy.js';
import { planReach, topScorer, upcomingGames } from '@shared/gamePlan.js';
import { per100, shotTakers } from '@shared/scoutReport.js';
import { possessions } from '@shared/teamStats.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import { gameDayText } from '@/utils/nextGames';
import { positions } from '@/utils/positions';
import HelpLink from '@/components/SHARED/help-link/help-link.component';
import TeamHeader from '@/components/SHARED/team-header/team-header.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

// The scouting report on a team, for planning a game against it: what it
// runs, how it plays, who takes its shots and how it has done against
// each defense and offense. Public information only (team.scoutLog,
// season stats and summary/teamStats).
const ScoutReport = ({ league, teams, teamStats, schedule, myTeamId, base, teamLink, gameLink, playerLink }) => {

    const { teamId } = useParams();
    const [ team, setTeam ] = useState(undefined);

    useEffect(() => {
        setTeam(undefined);
        let unsub;
        listenToDoc({ 'path': `leagues/${league.id}/teams/${teamId}`, 'callback': setTeam, 'handleListener': (u) => { unsub = u; } });
        return () => { if (unsub) unsub(); };
    }, [league.id, teamId])

    if (team === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;
    if (team === null) return <p className='meta'>Team not found.</p>;

    const year = league.season.year ?? 1;
    const log = team.scoutLog?.year === year ? team.scoutLog : null;
    const own = teamStats?.teams?.[teamId];
    const next = upcomingGames(schedule?.games, myTeamId, league.season.day ?? 0, planReach(league))
        .find(g => g.home === teamId || g.away === teamId);
    const nameOf = (id) => teams?.[id]?.abbr ?? teams?.[id]?.name ?? '?';

    return (
        <div className='manager-league scout-report'>
            <TeamHeader league={league} teams={teams} team={team} meta='Scouting report' />

            {next && teamId !== myTeamId &&
                <div className='g-card scout-next'>
                    <span>
                        You play them {next.home === myTeamId ? 'at home' : 'away'}{next.neutral ? ' (neutral site)' : ''},{' '}
                        {gameDayText(league, next.day)?.replace(/^(Today|Tomorrow)/, w => w.toLowerCase()) ?? `on day ${next.day}`}.
                    </span>
                    <Link to={`${base}/strategy?game=${next.id}#game-plans`} className='g-button primary'>Plan this game</Link>
                </div>
            }

            {!log ?
                <div className='g-card'><p className='meta'>They haven&apos;t played yet this season. Check back after their first game.</p></div> :
                <>
                    <Tendencies log={log} />
                    <Style teamId={teamId} teamStats={teamStats} />
                    <Shooters team={team} playerLink={playerLink} />
                    <VsTable title='Against each defense' note='How they scored (points per 100 possessions) against each defense they faced.'
                        lines={log.vs?.defense} list={DEFENSES} avg={own && per100({ pts: own.pts, poss: possessions(own) })} better='high' />
                    <VsTable title='Against each offense' note='Points per 100 possessions they allowed against each offense they faced.'
                        lines={log.vs?.offense} list={OFFENSES} avg={own && per100({ pts: own.opp.pts, poss: possessions(own.opp) })} better='low' />
                    {log.vs?.keyed &&
                        <VsTable title='When a defense keyed on one of them'
                            note='How they scored when the other team focused its defense on one of their players.'
                            lines={{ keyed: log.vs.keyed }} list={[{ key: 'keyed', label: 'Keyed on' }]}
                            avg={own && per100({ pts: own.pts, poss: possessions(own) })} better='high' />
                    }
                    <LastGames log={log} team={team} nameOf={nameOf} gameLink={gameLink} teamLink={teamLink} />
                </>
            }
        </div>
    )
}

const count = (n) => `${n} game${n === 1 ? '' : 's'}`;

// What they ran this season, most used first.
const Tendencies = ({ log }) => {
    const row = (cat, list) => Object.entries(log.ran?.[cat] ?? {})
        .sort((a, b) => b[1] - a[1])
        .map(([key, n]) => `${strategyLabel(list, key)} ${n}`)
        .join(' · ') || '-';
    const games = Object.values(log.ran?.offense ?? {}).reduce((s, n) => s + n, 0);
    const keyed = log.ran?.keyed ?? 0;
    return (
        <div className='g-card'>
            <h3>What they run <HelpLink section='game-plans' label='How game plans work' /></h3>
            <dl className='scout-list'>
                <dt>Offense</dt><dd>{row('offense', OFFENSES)}</dd>
                <dt>Defense</dt><dd>{row('defense', DEFENSES)}</dd>
                <dt>Pace</dt><dd>{row('pace', PACES)}</dd>
                <dt>Key on</dt><dd>{keyed ? `Keyed on a player in ${count(keyed)}` : 'Never keyed on a player'}</dd>
            </dl>
            <p className='meta'>Games this season: {games}. Their last three games are at the bottom of the page.</p>
        </div>
    )
};

// Style numbers per game with league ranks (1 = most).
const STYLE = [
    { label: 'Possessions a game', value: (s) => possessions(s) / s.gp, digits: 1, rank: 'most' },
    { label: 'Points per 100 possessions', value: (s) => 100 * s.pts / possessions(s), digits: 1, rank: 'best' },
    { label: 'Allowed per 100 possessions', value: (s) => 100 * s.opp.pts / possessions(s.opp), digits: 1, rank: 'best', low: true },
    { label: 'Shots that are threes', value: (s) => (s.fga ? 100 * s.tpa / s.fga : 0), digits: 0, suffix: '%', rank: 'most' },
    { label: 'Three-point %', value: (s) => (s.tpa ? 100 * s.tpm / s.tpa : 0), digits: 1, suffix: '%', rank: 'best' },
    { label: 'Free throws a game', value: (s) => s.fta / s.gp, digits: 1, rank: 'most' },
    { label: 'Assists a game', value: (s) => s.ast / s.gp, digits: 1, rank: 'most' },
    { label: 'Turnovers a game', value: (s) => s.tov / s.gp, digits: 1, rank: 'fewest', low: true },
    { label: 'Offensive rebounds a game', value: (s) => s.oreb / s.gp, digits: 1, rank: 'most' },
];

// 1st, 2nd, 3rd, 4th, ..., 11th, 12th, 13th, 21st
const ordinal = (n) => {
    if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
    return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

const Style = ({ teamId, teamStats }) => {
    const all = Object.entries(teamStats?.teams ?? {}).filter(([, s]) => s.gp > 0);
    const own = teamStats?.teams?.[teamId];
    if (!own?.gp) return null;
    return (
        <div className='g-card'>
            <h3>How they play</h3>
            <div className='table-scroll'>
                <table className='scout-table'>
                    <thead><tr><th>Per game</th><th className='num'>Them</th><th className='num'>League rank</th></tr></thead>
                    <tbody>
                        {STYLE.map(m => {
                            const values = all.map(([id, s]) => [id, m.value(s)]);
                            values.sort((a, b) => (m.low ? a[1] - b[1] : b[1] - a[1]));
                            const rank = values.findIndex(([id]) => id === teamId) + 1;
                            return (
                                <tr key={m.label}>
                                    <td>{m.label}</td>
                                    <td className='num'>{m.value(own).toFixed(m.digits)}{m.suffix ?? ''}</td>
                                    <td className='num meta'>{ordinal(rank)} {m.rank} of {values.length}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
};

// Who takes the shots, and how much the scoring leans on the top scorer.
const Shooters = ({ team, playerLink }) => {
    const list = shotTakers(team, 6);
    if (!list.length) return null;
    const { id, reliance } = topScorer(team);
    const top = team.players[id];
    const pct = (x) => `${Math.round(100 * x)}%`;
    return (
        <div className='g-card'>
            <h3>Who takes the shots</h3>
            {top && list.length > 1 &&
                <p className='scout-lean'>
                    {top.fName} {top.lName} scores <b>{reliance.toFixed(1)} times</b> as much as anyone else on the team.
                </p>
            }
            <div className='table-scroll'>
                <table className='scout-table'>
                    <thead>
                        <tr><th>Player</th><th className='num'>PPG</th><th className='num'>Team pts</th><th className='num'>Team shots</th><th className='num'>Threes</th></tr>
                    </thead>
                    <tbody>
                        {list.map(p => (
                            <tr key={p.id}>
                                <td>
                                    <Link to={playerLink(team.id, p.id)}>{p.name}</Link>{' '}
                                    <span className='meta'>{positions[p.pos1 - 1].short}</span>
                                    {p.injured && <span className='scout-injured'> Injured</span>}
                                </td>
                                <td className='num'>{p.ppg.toFixed(1)}</td>
                                <td className='num'>{pct(p.ptsShare)}</td>
                                <td className='num'>{pct(p.fgaShare)}</td>
                                <td className='num'>{pct(p.tpaShare)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className='meta'>Team pts and team shots: the player&apos;s share of the team&apos;s points and shots. Threes: share of the player&apos;s own shots that are threes.</p>
        </div>
    )
};

// Results against each choice the other teams made, next to the season
// average (better or worse than usual).
const VsTable = ({ title, note, lines, list, avg, better }) => {
    const rows = list.filter(o => lines?.[o.key]?.g);
    return (
        <div className='g-card'>
            <h3>{title}</h3>
            {rows.length === 0 ? <p className='meta'>No games yet.</p> :
                <div className='table-scroll'>
                    <table className='scout-table'>
                        <thead><tr><th></th><th className='num'>G</th><th className='num'>W-L</th><th className='num'>Per 100</th><th className='num'>vs avg</th></tr></thead>
                        <tbody>
                            {rows.map(o => {
                                const l = lines[o.key];
                                const v = per100(l);
                                const diff = avg != null && v != null ? v - avg : null;
                                const good = diff != null && (better === 'high' ? diff > 0 : diff < 0);
                                return (
                                    <tr key={o.key}>
                                        <td>{o.label}</td>
                                        <td className='num'>{l.g}</td>
                                        <td className='num'>{l.w}-{l.g - l.w}</td>
                                        <td className='num'>{v?.toFixed(1) ?? '-'}</td>
                                        <td className={`num ${diff == null || Math.abs(diff) < 0.05 ? '' : good ? 'scout-good' : 'scout-bad'}`}>
                                            {diff == null ? '-' : `${diff > 0 ? '+' : ''}${diff.toFixed(1)}`}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            }
            <p className='meta'>{note} A few games can be luck, so look for a pattern.</p>
        </div>
    )
};

// Their last three games: what they ran and what they faced.
const LastGames = ({ log, team, nameOf, gameLink, teamLink }) => {
    const plan = (s, keyName) => {
        if (!s?.offense) return '-';
        const parts = [strategyLabel(OFFENSES, s.offense), strategyLabel(DEFENSES, s.defense), `${strategyLabel(PACES, s.pace)} pace`];
        if (s.keyOn) parts.push(`keyed on ${keyName ?? 'a player'}`);
        return parts.join(' · ');
    };
    const ownName = (id) => {
        const p = team.players?.[id];
        return p ? `${p.fName} ${p.lName}` : null;
    };
    return (
        <div className='g-card'>
            <h3>Last three games</h3>
            <ul className='scout-games'>
                {(log.last ?? []).slice(0, 3).map(g => (
                    <li key={g.g}>
                        <div>
                            <span className={g.w ? 'scout-good' : 'scout-bad'}>{g.w ? 'W' : 'L'}</span>{' '}
                            <Link to={gameLink(g.g)}>{g.s}-{g.os}</Link> vs <Link to={teamLink(g.o)}>{nameOf(g.o)}</Link>
                            <span className='meta'> · day {g.d}</span>
                        </div>
                        <div className='meta'><b>Ran:</b> {plan(g.ran, g.ran?.keyOnName)}</div>
                        <div className='meta'><b>Faced:</b> {plan(g.faced, g.faced?.keyOnName ?? ownName(g.faced?.keyOn))}</div>
                    </li>
                ))}
            </ul>
        </div>
    )
};

export default ScoutReport
